import request, { _POST } from "@/services/request";
import type { CopywritingLength } from "@/services/module/copywritingAgent";

const BASE = "/admin/promptStyle";

type PromptStyleLength = "LENGTH_INVALID" | "SHORT" | "MEDIUM" | "LONG";

type PromptStyleStatus = "STATUS_INVALID" | "ENABLED" | "DISABLED";

type PromptStyleApiItem = {
  id?: string;
  name?: string;
  length?: PromptStyleLength;
  status?: PromptStyleStatus;
  systemPrompt?: string;
  examples?: Array<{ good?: string }>;
};

type PromptStyleListApiReq = {
  name?: string;
  length?: PromptStyleLength;
  status?: PromptStyleStatus;
  page?: number;
  size?: number;
};

type PromptStyleListApiRsp = {
  list?: PromptStyleApiItem[];
  total?: string;
  page?: number;
  size?: number;
};

type PromptStyleSaveApiReq = {
  name?: string;
  length?: PromptStyleLength;
  status?: PromptStyleStatus;
  systemPrompt?: string;
  examples?: Array<{ good: string }>;
};

type PromptStyleUpdateApiReq = PromptStyleSaveApiReq & { id: string };

type PromptStyleDeleteApiReq = { id: string };

const LENGTH_TO_API: Record<CopywritingLength, PromptStyleLength> = {
  short: "SHORT",
  medium: "MEDIUM",
  long: "LONG",
};

const LENGTH_FROM_API: Record<string, CopywritingLength> = {
  SHORT: "short",
  MEDIUM: "medium",
  LONG: "long",
  PROMPT_STYLE_LENGTH_SHORT: "short",
  PROMPT_STYLE_LENGTH_MEDIUM: "medium",
  PROMPT_STYLE_LENGTH_LONG: "long",
};

const STATUS_TO_API: Record<"enabled" | "disabled", PromptStyleStatus> = {
  enabled: "ENABLED",
  disabled: "DISABLED",
};

const STATUS_FROM_API: Record<string, "enabled" | "disabled"> = {
  ENABLED: "enabled",
  DISABLED: "disabled",
  PROMPT_STYLE_STATUS_ENABLED: "enabled",
  PROMPT_STYLE_STATUS_DISABLED: "disabled",
};

/** 前端使用的风格配置（与文案生成 agent 对齐） */
export type CopywritingStyleConfig = {
  id: string;
  name: string;
  systemPrompt: string;
  examples: Array<{ good: string }>;
  length: CopywritingLength;
  status: "enabled" | "disabled";
};

export type CopywritingStyleListReq = {
  keyword?: string;
  length?: CopywritingLength;
  status?: "enabled" | "disabled";
  page?: number;
  size?: number;
};

export type CopywritingStyleListRsp = {
  list: CopywritingStyleConfig[];
  total: string;
  page?: number;
  size?: number;
};

export type CopywritingStyleCreateReq = Omit<CopywritingStyleConfig, "id">;

export type CopywritingStyleUpdateReq = Partial<CopywritingStyleConfig> & { id: string };

export type CopywritingStyleDeleteReq = { id: string };

export type CopywritingStyleUpdateStatusReq = {
  id: string;
  status: "enabled" | "disabled";
};

type PromptStyleUpdateStatusApiReq = {
  id: string;
  status: PromptStyleStatus;
};

function toApiLength(length?: CopywritingLength) {
  if (!length) return undefined;
  return LENGTH_TO_API[length];
}

function toApiStatus(status?: "enabled" | "disabled") {
  if (!status) return undefined;
  return STATUS_TO_API[status];
}

function toApiSaveReq(params: CopywritingStyleCreateReq | CopywritingStyleUpdateReq): PromptStyleSaveApiReq {
  const payload: PromptStyleSaveApiReq = {};
  if (params.name !== undefined) payload.name = params.name;
  if (params.length !== undefined) payload.length = toApiLength(params.length);
  if (params.status !== undefined) payload.status = toApiStatus(params.status);
  if (params.systemPrompt !== undefined) payload.systemPrompt = params.systemPrompt;
  if (params.examples !== undefined) payload.examples = params.examples;
  return payload;
}

function fromApiItem(item: PromptStyleApiItem): CopywritingStyleConfig {
  return {
    id: item.id ?? "",
    name: item.name ?? "",
    systemPrompt: item.systemPrompt ?? "",
    examples: (item.examples ?? []).map((example) => ({ good: example.good ?? "" })),
    length: (item.length && LENGTH_FROM_API[item.length]) || "medium",
    status: (item.status && STATUS_FROM_API[item.status]) || "disabled",
  };
}

function toApiListReq(params?: CopywritingStyleListReq): PromptStyleListApiReq {
  return {
    name: params?.keyword,
    length: toApiLength(params?.length),
    status: toApiStatus(params?.status),
    page: params?.page,
    size: params?.size,
  };
}

/** 风格配置列表 */
const listCopywritingStyle = async (params?: CopywritingStyleListReq) => {
  const res = await request<PromptStyleListApiRsp, PromptStyleListApiReq>(_POST, `${BASE}/list`)(toApiListReq(params));
  return {
    ...res,
    data: {
      list: (res.data.list ?? []).map(fromApiItem),
      total: res.data.total ?? "0",
      page: res.data.page,
      size: res.data.size,
    },
  };
};

/** 创建风格配置 */
const createCopywritingStyle = (params: CopywritingStyleCreateReq) => {
  return request<null, PromptStyleSaveApiReq>(_POST, `${BASE}/save`)(toApiSaveReq(params));
};

/** 更新风格配置 */
const updateCopywritingStyle = (params: CopywritingStyleUpdateReq) => {
  const payload: PromptStyleUpdateApiReq = {
    id: params.id,
    ...toApiSaveReq(params),
  };
  return request<null, PromptStyleUpdateApiReq>(_POST, `${BASE}/save`)(payload);
};

/** 切换风格启用状态 */
const updateCopywritingStyleStatus = (params: CopywritingStyleUpdateStatusReq) => {
  return request<null, PromptStyleUpdateStatusApiReq>(
    _POST,
    `${BASE}/updateStatus`
  )({
    id: params.id,
    status: toApiStatus(params.status)!,
  });
};

/** 删除风格配置 */
const deleteCopywritingStyle = (params: CopywritingStyleDeleteReq) => {
  return request<null, PromptStyleDeleteApiReq>(_POST, `${BASE}/delete`)(params);
};

const copywritingStyleApis = {
  listCopywritingStyle,
  createCopywritingStyle,
  updateCopywritingStyle,
  updateCopywritingStyleStatus,
  deleteCopywritingStyle,
};

export default copywritingStyleApis;
